import React from "react";
import { useTranslation } from "react-i18next";
import type { TextProps } from "../Text";
import { Text } from "../Text";

export interface LangProps extends Omit<TextProps, "children"> {
  /**
   * Translation key for i18n
   */
  text: string;

  /**
   * Namespace for translation (optional)
   */
  namespace?: string;

  /**
   * Interpolation values for dynamic translations
   */
  values?: Record<string, any>;

  /**
   * Default text if translation key is not found
   */
  defaultText?: string;

  /**
   * Convert to uppercase
   */
  uppercase?: boolean;

  /**
   * Convert to lowercase
   */
  lowercase?: boolean;

  /**
   * Capitalize first letter
   */
  capitalize?: boolean;
}

const LangComponent: React.FC<LangProps> = ({
  text,
  namespace,
  values,
  defaultText,
  uppercase = false,
  lowercase = false,
  capitalize = false,
  className,
  "data-testid": dataTestId,
  ...textProps
}) => {
  const { t } = useTranslation(namespace);

  // Get translated text
  let translatedText = t(text, {
    defaultValue: defaultText || text,
    ...values,
  });

  // Apply text transformations
  if (uppercase) {
    translatedText = translatedText.toUpperCase();
  } else if (lowercase) {
    translatedText = translatedText.toLowerCase();
  } else if (capitalize) {
    translatedText =
      translatedText.charAt(0).toUpperCase() + translatedText.slice(1);
  }

  return (
    <Text className={className} data-testid={dataTestId} {...textProps}>
      {translatedText}
    </Text>
  );
};

// Apply memoization with React.memo
export const Lang = React.memo(LangComponent);

// Set display name for debugging
(Lang as any).displayName = "Lang";
